import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link as RouterLink} from 'react-router-dom';
import {
    Box, Typography, TextField, Button, Paper, Stack, Alert,
    Snackbar, CircularProgress, Grid,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import apiService from '../../services/apiService';
import EntityCriteriaEditor from '../../components/EntityCriteriaEditor';
import {ProtectedFieldSet} from '../../context/AuthDisabledContext';
import Protected from '../../components/Protected';
import {ROLES} from '../../config/roles';

const XpLevelConfigEditPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const requiredAction = isNew ? 'create' : 'update';

    const [levelNumber, setLevelNumber] = useState('');
    const [xpThreshold, setXpThreshold] = useState('');
    const [reward, setReward] = useState([]);

    const [loading, setLoading] = useState(!isNew);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: ''});

    useEffect(() => {
        if (!isNew) {
            apiService.get(`/api/xp-level-configs/${id}`)
                .then(data => {
                    setLevelNumber(String(data.levelNumber ?? ''));
                    setXpThreshold(String(data.xpThreshold ?? ''));
                    setReward(data.reward || []);
                })
                .catch(err => setError(err.message || 'Failed to load level config'))
                .finally(() => setLoading(false));
        }
    }, [id, isNew]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitted(true);
        setIsSaving(true);
        setError(null);

        const rewardError = EntityCriteriaEditor.validate(reward);
        if (rewardError) {
            setError('Reward: ' + rewardError);
            setIsSaving(false);
            return;
        }

        const data = {
            levelNumber: parseInt(levelNumber),
            xpThreshold: parseInt(xpThreshold),
            reward: reward.length > 0 ? reward : null,
        };

        try {
            const apiCall = isNew
                ? apiService.post('/api/xp-level-configs', data)
                : apiService.put(`/api/xp-level-configs/${id}`, data);
            await apiCall;
            setSnackbar({open: true, message: 'Level config saved!'});
            setTimeout(() => navigate('/xp-level-configs'), 800);
        } catch (err) {
            setError(err.message || 'Failed to save level config');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{p: 4, textAlign: 'center'}}><CircularProgress/></Box>;
    }

    return (
        <Box sx={{p: 2}} component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" gutterBottom>
                {isNew ? 'Create XP Level Config' : `Edit Level ${id}`}
            </Typography>

            <Protected component={XpLevelConfigEditPage} action={requiredAction} mode="disable">
                <ProtectedFieldSet>
                    <Grid container spacing={3}>
                        <Grid size={{xs: 12, md: 6}}>
                            <Stack spacing={3}>
                                <Paper sx={{p: 2}}>
                                    <Typography variant="h6" gutterBottom>Configuration</Typography>
                                    <Stack spacing={2}>
                                        <TextField
                                            required fullWidth
                                            label="Level Number"
                                            type="number"
                                            value={levelNumber}
                                            onChange={e => setLevelNumber(e.target.value)}
                                            disabled={!isNew}
                                            inputProps={{min: 1}}
                                        />
                                        <TextField
                                            required fullWidth
                                            label="XP Threshold"
                                            type="number"
                                            value={xpThreshold}
                                            onChange={e => setXpThreshold(e.target.value)}
                                            helperText="Cumulative total XP required to reach this level"
                                            inputProps={{min: 0}}
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Grid>

                        <Grid size={{xs: 12, md: 6}}>
                            <Paper sx={{p: 2}}>
                                <Typography variant="h6" gutterBottom>Level-Up Reward</Typography>
                                <EntityCriteriaEditor
                                    value={reward}
                                    onChange={setReward}
                                    preset="reward"
                                    showErrors={submitted}
                                />
                            </Paper>
                        </Grid>
                    </Grid>

                    {error && <Alert severity="error" sx={{mt: 3}}>{error}</Alert>}

                    <Stack direction="row" spacing={2} sx={{mt: 3}}>
                        <Protected component={XpLevelConfigEditPage} action={requiredAction}>
                            <Button
                                type="submit" variant="contained"
                                startIcon={isSaving ? <CircularProgress size={20}/> : <SaveIcon/>}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </Button>
                        </Protected>
                        <Button component={RouterLink} to="/xp-level-configs"
                                variant="outlined" startIcon={<ArrowBackIcon/>}>
                            Back to List
                        </Button>
                    </Stack>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({open: false, message: ''})}
                        message={snackbar.message}
                    />
                </ProtectedFieldSet>
            </Protected>
        </Box>
    );
};

XpLevelConfigEditPage.abilities = {
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};

export default XpLevelConfigEditPage;
